import { Component, EventEmitter, HostListener, Input, OnChanges, OnInit, Output, SimpleChanges, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import {
  BusinessFeature,
  FeatureCollection,
  FeatureCollectionGroup,
  MAX_SPACE_FEATURES,
  SalaService,
  SpaceFeature,
  SpaceFeatureIcon,
  SpaceFeatureInput,
} from '../../services/sala.service';
import { NotificationService } from '../../services/notification.service';
import { DialogService } from '../../services/dialog.service';
import { AuthService } from '../../services/auth.service';

interface EditableFeature {
  uid: number;
  business_feature_id: number | null;
  name: string;
  description: string;
  icon: string;
}

interface IconGroup {
  name: string;
  icons: SpaceFeatureIcon[];
}

interface CollectionSection {
  group: FeatureCollectionGroup | null;
  collections: FeatureCollection[];
}

interface CollectionForm {
  name: string;
  description: string;
  group_id: number | null;
  iconIds: string[];
}

interface GroupForm {
  name: string;
}

const normalizeName = (value: string) => value
  .trim()
  .toLocaleLowerCase('es')
  .normalize('NFD')
  .replace(/[̀-ͯ]/g, '');

/**
 * Características INCLUIDAS de un espacio (sin costo): Wi-Fi, pantalla,
 * aire acondicionado... Se eligen haciendo clic directamente sobre el
 * catálogo de iconos y se muestran así en la ficha pública (máximo
 * MAX_SPACE_FEATURES para no saturarla). Los servicios con costo siguen
 * siendo «Extras».
 *
 * Las colecciones son conjuntos reutilizables de amenidades; sus grupos
 * opcionales permiten organizarlas (p. ej. «Salas de reunión»).
 *
 * Modos:
 *  - Con [spaceId]: carga y guarda contra el espacio (detalle del espacio).
 *  - [staged]=true: el espacio aún no existe (formulario de creación);
 *    emite la lista en (stagedChange) y el formulario la guarda al crear.
 */
@Component({
  selector: 'app-space-features-editor',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <section class="features-section" [class.compact]="staged" aria-labelledby="features-title">
      <header class="sf-head">
        <div class="sf-head-copy">
          <h2 id="features-title">Características incluidas <span class="sf-counter" [class.full]="items().length >= max">{{ items().length }} de {{ max }}</span></h2>
          <p>Marca los iconos de lo que sí incluye este espacio. Se muestran en la ficha pública sin costo adicional; los servicios con precio van en «Extras».</p>
        </div>
        <div class="sf-help-wrap">
          <button type="button" class="sf-help-toggle" [attr.aria-expanded]="helpOpen()" aria-controls="sf-help-pop"
            [attr.aria-label]="helpOpen() ? 'Cerrar ayuda de características' : 'Ver ayuda de características'" (click)="toggleHelp($event)">
            <i class="bi bi-question-lg" aria-hidden="true"></i>
          </button>
          <div class="sf-help-pop" id="sf-help-pop" *ngIf="helpOpen()" role="dialog" aria-label="Ayuda de características incluidas" (click)="$event.stopPropagation()">
            <div class="sf-help-pop-head">
              <span><i class="bi bi-question-circle" aria-hidden="true"></i> Guía rápida</span>
              <button type="button" class="sf-mini" (click)="closeHelp()" aria-label="Cerrar ayuda"><i class="bi bi-x-lg"></i></button>
            </div>
            <ul>
              <li><i class="bi bi-hand-index-thumb" aria-hidden="true"></i><span><strong>Amenidades disponibles:</strong> haz clic en un icono para incluirlo o quitarlo. Cada espacio muestra hasta {{ max }} y el orden de tu selección es el que verá el cliente.</span></li>
              <li><i class="bi bi-list-check" aria-hidden="true"></i><span><strong>Tu selección:</strong> revisa lo marcado, arrastra o usa las flechas para ordenar y la X para quitar una amenidad. El botón «Quitar todas» vacía la lista.</span></li>
              <li><i class="bi bi-folder-plus" aria-hidden="true"></i><span><strong>Nuevo grupo:</strong> solo organiza tus colecciones (por ejemplo, «Salas de reunión»). No cambia lo que ve el cliente.</span></li>
              <li><i class="bi bi-plus-circle" aria-hidden="true"></i><span><strong>Nueva colección:</strong> guarda una combinación reutilizable de amenidades. Luego pulsa «Aplicar» para marcarlas todas de una vez en este espacio.</span></li>
              <li><i class="bi bi-save" aria-hidden="true"></i><span><strong>Guardar características:</strong> confirma los cambios del espacio. Si descartas, la selección vuelve a como estaba guardada.</span></li>
            </ul>
          </div>
        </div>
      </header>

      <p class="sf-state" *ngIf="loading()">Cargando características…</p>
      <p class="sf-state sf-error" *ngIf="loadError()">{{ loadError() }} <button type="button" class="sf-link" (click)="reload()">Reintentar</button></p>

      <ng-container *ngIf="!loading() && !loadError()">
        <aside class="sf-info">
          <i class="bi bi-info-circle" aria-hidden="true"></i>
          <div>
            <strong>Haz clic para incluir o quitar una amenidad</strong>
            <span>El icono seleccionado es exactamente lo que verá el cliente en la ficha del espacio. Puedes ordenar tu selección; el orden se respeta en el front.</span>
          </div>
        </aside>

        <div class="sf-workspace">
          <section class="sf-catalog" aria-labelledby="sf-catalog-title">
            <div class="sf-catalog-head">
              <div>
                <h3 id="sf-catalog-title">Amenidades disponibles</h3>
                <p>Elige hasta {{ max }}. Cada icono tiene un significado fijo para que el cliente lo entienda igual en todos los espacios.</p>
              </div>
              <label class="sf-search">
                <i class="bi bi-search" aria-hidden="true"></i>
                <input type="search" placeholder="Buscar amenidad..." aria-label="Buscar amenidad"
                  [ngModel]="iconQuery()" (ngModelChange)="iconQuery.set($event)">
              </label>
            </div>

            <p class="sf-state" *ngIf="!icons().length">No se pudo cargar el catálogo de iconos.</p>
            <p class="sf-state" *ngIf="icons().length && !iconGroups().length">No hay amenidades que coincidan con «{{ iconQuery() }}».</p>

            <div class="sf-catalog-body">
              <section class="sf-icon-group" *ngFor="let group of iconGroups(); trackBy: trackByIconGroup">
                <h4>{{ group.name }} <span>{{ group.icons.length }}</span></h4>
                <div class="sf-icon-grid">
                  <button type="button" class="sf-icon-option"
                    *ngFor="let icon of group.icons; trackBy: trackByIcon"
                    [class.on]="isIconSelected(icon.id)"
                    [disabled]="!isIconSelected(icon.id) && items().length >= max"
                    [attr.aria-pressed]="isIconSelected(icon.id)"
                    [title]="isIconSelected(icon.id) ? 'Quitar ' + icon.label : (items().length >= max ? 'Ya tienes ' + max + ' amenidades' : 'Incluir ' + icon.label)"
                    (click)="toggleIcon(icon)">
                    <span class="sf-icon-tile">
                      <i class="bi" [ngClass]="'bi-' + icon.id" aria-hidden="true"></i>
                      <i class="bi bi-check-lg sf-icon-check" aria-hidden="true"></i>
                    </span>
                    <span class="sf-icon-label">{{ icon.label }}</span>
                  </button>
                </div>
              </section>
            </div>
          </section>

          <aside class="sf-side" aria-labelledby="sf-selected-title">
            <div class="sf-side-head">
              <div>
                <h3 id="sf-selected-title">Tu selección <span class="sf-side-counter" [class.full]="items().length >= max">{{ items().length }} de {{ max }}</span></h3>
                <p>{{ items().length ? 'Así se verá en la ficha pública. Arrastra o usa las flechas para ordenar.' : 'Aún no has incluido amenidades en este espacio.' }}</p>
              </div>
              <button type="button" class="sf-link" *ngIf="items().length" (click)="clearAll()">Quitar todas</button>
            </div>

            <div class="sf-side-empty" *ngIf="!items().length">
              <i class="bi bi-hand-index-thumb" aria-hidden="true"></i>
              <strong>Empieza con un icono</strong>
              <span>Haz clic en una amenidad de la izquierda; aparecerá aquí y podrás ordenarla antes de guardar.</span>
            </div>

            <ol class="sf-list" *ngIf="items().length" aria-label="Amenidades incluidas, en orden de aparición">
              <li *ngFor="let item of items(); let i = index; let first = first; let last = last; trackBy: trackByUid"
                draggable="true"
                [class.dragging]="dragIndex === i" [class.drop-target]="overIndex === i && dragIndex !== i"
                (dragstart)="onDragStart(i, $event)" (dragover)="onDragOver(i, $event)"
                (drop)="onDrop(i, $event)" (dragend)="onDragEnd()">
                <span class="sf-order" aria-hidden="true" title="Arrastra para ordenar">{{ i + 1 }}</span>
                <span class="sf-icon"><i class="bi" [ngClass]="'bi-' + item.icon" aria-hidden="true"></i></span>
                <span class="sf-text">
                  <span class="sf-name"><strong [title]="item.name">{{ item.name }}</strong><span class="sf-badge" *ngIf="item.business_feature_id" title="General del negocio">General</span></span>
                  <small *ngIf="item.description" [title]="item.description">{{ item.description }}</small>
                </span>
                <span class="sf-actions">
                  <button type="button" class="sf-mini" (click)="move(i, -1)" [disabled]="first" [attr.aria-label]="'Mover antes ' + item.name" title="Mover antes"><i class="bi bi-chevron-up"></i></button>
                  <button type="button" class="sf-mini" (click)="move(i, 1)" [disabled]="last" [attr.aria-label]="'Mover después ' + item.name" title="Mover después"><i class="bi bi-chevron-down"></i></button>
                  <button type="button" class="sf-mini danger" (click)="remove(i)" [attr.aria-label]="'Quitar ' + item.name + ' de este espacio'" title="Quitar de este espacio"><i class="bi bi-x-lg"></i></button>
                </span>
              </li>
            </ol>

            <div class="sf-side-actions">
              <div class="sf-side-actions-copy">
                <strong>Colecciones</strong>
                <span>Guarda combinaciones de amenidades para reutilizarlas en otros espacios.</span>
              </div>
              <div class="sf-side-actions-buttons">
                <button type="button" class="sf-btn" (click)="openGroupCreate()"><i class="bi bi-folder-plus" aria-hidden="true"></i> Nuevo grupo</button>
                <button type="button" class="sf-btn primary" (click)="openCollectionCreate()"><i class="bi bi-plus-lg" aria-hidden="true"></i> Nueva colección</button>
              </div>
            </div>
          </aside>
        </div>

        <section class="sf-collections" aria-labelledby="sf-collections-title">
          <div class="sf-collections-head">
            <div>
              <h3 id="sf-collections-title">Colecciones guardadas</h3>
              <p>Aplica varias amenidades de una vez. Organízalas en grupos como «Salas de reunión» o «Eventos».</p>
            </div>
          </div>

          <p class="sf-state sf-error" *ngIf="collectionError()">{{ collectionError() }}</p>
          <p class="sf-state" *ngIf="!collectionError() && !collections().length">Todavía no hay colecciones. Crea una para reutilizar el mismo conjunto de amenidades en varios espacios.</p>

          <div class="sf-collection-section" *ngFor="let section of collectionSections(); trackBy: trackByCollectionSection">
            <div class="sf-collection-section-head">
              <strong>{{ section.group ? section.group.name : 'Sin grupo' }}</strong>
              <span>{{ section.collections.length }} colección(es)</span>
              <ng-container *ngIf="section.group as group">
                <button type="button" class="sf-mini" (click)="openGroupEdit(group)" [attr.aria-label]="'Renombrar ' + group.name" title="Renombrar grupo"><i class="bi bi-pencil"></i></button>
                <button type="button" class="sf-mini danger" (click)="deleteGroup(group)" [attr.aria-label]="'Eliminar ' + group.name" title="Eliminar grupo"><i class="bi bi-trash"></i></button>
              </ng-container>
            </div>
            <div class="sf-collection-grid">
              <article class="sf-collection-card" *ngFor="let collection of section.collections; trackBy: trackByCollection">
                <div class="sf-collection-icons">
                  <span *ngFor="let iconId of collection.icons.slice(0, 5)" [title]="iconLabel(iconId)" class="sf-collection-icon"><i class="bi" [ngClass]="'bi-' + iconId" aria-hidden="true"></i></span>
                  <span class="sf-collection-more" *ngIf="collection.icons.length > 5">+{{ collection.icons.length - 5 }}</span>
                </div>
                <div class="sf-collection-text">
                  <strong>{{ collection.name }}</strong>
                  <small>{{ collection.icons.length }} amenidad(es)<ng-container *ngIf="collection.description"> · {{ collection.description }}</ng-container></small>
                </div>
                <div class="sf-collection-card-actions">
                  <button type="button" class="sf-btn apply" (click)="applyCollection(collection)" [disabled]="items().length >= max"><i class="bi bi-plus-circle" aria-hidden="true"></i> Aplicar</button>
                  <button type="button" class="sf-mini" (click)="openCollectionEdit(collection)" [attr.aria-label]="'Editar ' + collection.name" title="Editar colección"><i class="bi bi-pencil"></i></button>
                  <button type="button" class="sf-mini danger" (click)="deleteCollection(collection)" [attr.aria-label]="'Eliminar ' + collection.name" title="Eliminar colección"><i class="bi bi-trash"></i></button>
                </div>
              </article>
            </div>
            <p class="sf-empty-group" *ngIf="!section.collections.length">Este grupo aún no tiene colecciones.</p>
          </div>
        </section>
      </ng-container>

      <footer class="sf-foot" *ngIf="!staged && dirty()">
        <span>Tienes cambios sin guardar.</span>
        <button type="button" class="sf-btn ghost" (click)="reload()" [disabled]="saving()">Descartar</button>
        <button type="button" class="sf-btn primary" (click)="save()" [disabled]="saving()">{{ saving() ? 'Guardando…' : 'Guardar características' }}</button>
      </footer>
    </section>

    <!-- Modal: crear / editar colección -->
    <div class="sf-overlay" *ngIf="collectionModalOpen()" (click)="closeCollectionModal()">
      <section class="sf-modal sf-collection-modal" role="dialog" aria-modal="true" aria-labelledby="sf-collection-title" (click)="$event.stopPropagation()" (keydown.enter)="onCollectionEnter($event)">
        <header>
          <h3 id="sf-collection-title">{{ editingCollectionId ? 'Editar colección' : 'Nueva colección' }}</h3>
          <button type="button" class="sf-mini" (click)="closeCollectionModal()" aria-label="Cerrar"><i class="bi bi-x-lg"></i></button>
        </header>
        <p class="sf-help">Una colección agrupa amenidades que sueles usar juntas. Aplicarla a un espacio las marca de una sola vez.</p>

        <label class="sf-label" for="sf-collection-name">Nombre de la colección *</label>
        <input id="sf-collection-name" name="collectionName" class="sf-input" [(ngModel)]="collectionForm.name" maxlength="80" placeholder="Ej. Oficina básica" data-no-limit-hint>

        <div class="sf-form-row">
          <label class="sf-label" for="sf-collection-group">Grupo <small>(opcional)</small></label>
          <select id="sf-collection-group" name="collectionGroup" class="sf-input" [(ngModel)]="collectionForm.group_id">
            <option [ngValue]="null">Sin grupo</option>
            <option *ngFor="let group of groups(); trackBy: trackByGroup" [ngValue]="group.group_id">{{ group.name }}</option>
          </select>
        </div>

        <label class="sf-label" for="sf-collection-desc">Descripción <small>(opcional)</small></label>
        <input id="sf-collection-desc" name="collectionDescription" class="sf-input" [(ngModel)]="collectionForm.description" maxlength="200" placeholder="Ej. Lo esencial para una reunión corta" data-no-limit-hint>

        <div class="sf-pick-head">
          <span class="sf-label">Amenidades de la colección *</span>
          <span>{{ collectionForm.iconIds.length }} de {{ max }}</span>
        </div>
        <p class="sf-help">Haz clic en los iconos que formarán la colección.</p>
        <div class="sf-pick-body">
          <section class="sf-icon-group" *ngFor="let group of allIconGroups(); trackBy: trackByIconGroup">
            <h4>{{ group.name }}</h4>
            <div class="sf-icon-grid compact-grid">
              <button type="button" class="sf-icon-option" *ngFor="let icon of group.icons; trackBy: trackByIcon"
                [class.on]="collectionForm.iconIds.includes(icon.id)"
                [disabled]="!collectionForm.iconIds.includes(icon.id) && collectionForm.iconIds.length >= max"
                [attr.aria-pressed]="collectionForm.iconIds.includes(icon.id)"
                [title]="icon.label"
                (click)="toggleCollectionIcon(icon.id)">
                <span class="sf-icon-tile">
                  <i class="bi" [ngClass]="'bi-' + icon.id" aria-hidden="true"></i>
                  <i class="bi bi-check-lg sf-icon-check" aria-hidden="true"></i>
                </span>
                <span class="sf-icon-label">{{ icon.label }}</span>
              </button>
            </div>
          </section>
        </div>

        <p class="sf-error" *ngIf="collectionModalError()" role="alert">{{ collectionModalError() }}</p>
        <footer>
          <button type="button" class="sf-btn ghost" (click)="closeCollectionModal()">Cancelar</button>
          <button type="button" class="sf-btn primary" (click)="saveCollection()" [disabled]="collectionBusy()">{{ collectionBusy() ? 'Guardando…' : 'Guardar colección' }}</button>
        </footer>
      </section>
    </div>

    <!-- Modal: crear / renombrar grupo de colecciones -->
    <div class="sf-overlay" *ngIf="groupModalOpen()" (click)="closeGroupModal()">
      <section class="sf-modal sf-group-modal" role="dialog" aria-modal="true" aria-labelledby="sf-group-title" (click)="$event.stopPropagation()" (keydown.enter)="onGroupEnter($event)">
        <header>
          <h3 id="sf-group-title">{{ editingGroupId ? 'Renombrar grupo' : 'Nuevo grupo de colecciones' }}</h3>
          <button type="button" class="sf-mini" (click)="closeGroupModal()" aria-label="Cerrar"><i class="bi bi-x-lg"></i></button>
        </header>
        <p class="sf-help">Los grupos solo organizan tus colecciones; no cambian lo que ve el cliente.</p>
        <label class="sf-label" for="sf-group-name">Nombre del grupo *</label>
        <input id="sf-group-name" name="groupName" class="sf-input" [(ngModel)]="groupForm.name" maxlength="60" placeholder="Ej. Salas de reunión" data-no-limit-hint>
        <p class="sf-error" *ngIf="groupModalError()" role="alert">{{ groupModalError() }}</p>
        <footer>
          <button type="button" class="sf-btn ghost" (click)="closeGroupModal()">Cancelar</button>
          <button type="button" class="sf-btn primary" (click)="saveGroup()" [disabled]="groupBusy()">{{ groupBusy() ? 'Guardando…' : 'Guardar grupo' }}</button>
        </footer>
      </section>
    </div>
  `,
  styles: [`
    :host { display: block; }
    .features-section { margin-bottom: 1.1rem; padding: 1.35rem; border-radius: 16px; border: 1px solid var(--ts-border-strong); background: var(--ts-bg-elevated); box-shadow: 0 10px 24px var(--ts-shadow-gray-1000-a16); }
    .features-section.compact { margin: 0; padding: 1rem; box-shadow: none; background: var(--ts-surface); }
    .compact .sf-catalog-body { max-height: 400px; }
    .sf-head { position: relative; display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem; padding-bottom: .9rem; margin-bottom: .9rem; border-bottom: 1px solid var(--ts-border); }
    .sf-head-copy { min-width: 0; }
    .sf-head h2 { display: flex; align-items: center; flex-wrap: wrap; gap: .5rem; margin: 0 0 .3rem; color: var(--ts-text); font-size: 1.1rem; }
    .compact .sf-head h2 { font-size: .98rem; }
    .sf-head p { margin: 0; max-width: 72ch; color: var(--ts-text-soft); font-size: .84rem; line-height: 1.5; }
    .sf-counter { padding: .1rem .55rem; border-radius: 999px; color: var(--ts-text-soft); background: var(--ts-surface); border: 1px solid var(--ts-border); font-size: .72rem; font-weight: 750; }
    .sf-counter.full { color: var(--ts-warning-text); border-color: var(--ts-warning-text); }
    .sf-help-wrap { position: relative; flex: none; }
    .sf-help-toggle { display: grid; place-items: center; width: 36px; height: 36px; min-height: 36px !important; border-radius: 50%; border: 1px solid var(--ts-border-strong); color: var(--ts-text-blue-250-3); background: var(--ts-surface); font: inherit; font-size: 1rem; cursor: pointer; transition: border-color .15s ease, background .15s ease, color .15s ease; }
    .sf-help-toggle:hover, .sf-help-toggle[aria-expanded="true"] { border-color: var(--ts-primary); color: var(--ts-text); background: var(--ts-bg-blue-400-a8); }
    .sf-help-toggle:focus-visible { outline: 2px solid var(--ts-primary); outline-offset: 2px; }
    .sf-help-pop { position: absolute; top: calc(100% + .55rem); right: 0; z-index: 20; width: min(440px, calc(100vw - 2rem)); padding: .9rem 1rem; border: 1px solid var(--ts-border-strong); border-radius: 14px; background: var(--ts-bg-elevated); box-shadow: 0 18px 46px var(--ts-shadow-gray-1000-a40); }
    .sf-help-pop-head { display: flex; align-items: center; justify-content: space-between; gap: .8rem; margin-bottom: .55rem; color: var(--ts-text); font-size: .84rem; font-weight: 800; }
    .sf-help-pop-head > span { display: inline-flex; align-items: center; gap: .4rem; }
    .sf-help-pop-head > span i { color: var(--ts-text-blue-250-3); }
    .sf-help-pop ul { display: grid; gap: .55rem; margin: 0; padding: 0; list-style: none; }
    .sf-help-pop li { display: grid; grid-template-columns: 20px minmax(0, 1fr); gap: .5rem; color: var(--ts-text-soft); font-size: .77rem; line-height: 1.45; }
    .sf-help-pop li > i { margin-top: .05rem; color: var(--ts-text-blue-250-3); font-size: .88rem; }
    .sf-help-pop li strong { color: var(--ts-text); }
    .sf-state { margin: .4rem 0; color: var(--ts-text-muted); font-size: .86rem; }
    .sf-error { color: var(--ts-danger-text); font-size: .84rem; }
    .sf-link { padding: 0; min-height: 0 !important; border: 0; background: none; color: var(--ts-text-blue-250-3); font: inherit; font-size: .8rem; font-weight: 750; cursor: pointer; }
    .sf-side-head .sf-link { flex: none; white-space: nowrap; }
    .sf-link:disabled { opacity: .45; cursor: not-allowed; }
    .sf-info { display: flex; align-items: flex-start; gap: .7rem; margin-bottom: .9rem; padding: .8rem .95rem; border: 1px solid var(--ts-primary-border); border-radius: 12px; background: var(--ts-bg-blue-400-a8); }
    .sf-info > i { margin-top: .1rem; color: var(--ts-text-blue-250-3); font-size: 1.1rem; }
    .sf-info div { display: grid; gap: .15rem; }
    .sf-info strong { color: var(--ts-text); font-size: .87rem; }
    .sf-info span { color: var(--ts-text-soft); font-size: .8rem; line-height: 1.45; }

    .sf-workspace { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 1rem; align-items: stretch; margin-bottom: 1rem; }
    .sf-catalog { display: flex; flex-direction: column; min-width: 0; height: 100%; box-sizing: border-box; padding: 1rem; border: 1px solid var(--ts-border-strong); border-radius: 14px; background: var(--ts-bg-elevated); box-shadow: 0 6px 18px var(--ts-shadow-gray-1000-a16); }
    .sf-catalog-head { display: flex; flex-direction: column; align-items: stretch; gap: .65rem; margin-bottom: .65rem; padding-bottom: .65rem; border-bottom: 1px solid var(--ts-border); }
    .sf-catalog-head h3, .sf-collections-head h3 { margin: 0 0 .15rem; color: var(--ts-text); font-size: .95rem; }
    .sf-catalog-head p, .sf-collections-head p { margin: 0; color: var(--ts-text-soft); font-size: .77rem; line-height: 1.4; }
    .sf-search { display: flex; align-items: center; gap: .45rem; flex: none; width: 100%; height: 38px; box-sizing: border-box; padding: 0 .75rem; border: 1px solid var(--ts-border-strong); border-radius: 9px; background: var(--ts-input-bg); color: var(--ts-text-muted); overflow: hidden; transition: border-color .15s ease, box-shadow .15s ease; }
    .sf-search:focus-within { border-color: var(--ts-primary); box-shadow: 0 0 0 3px var(--ts-bg-blue-400-a8); }
    .sf-search > i { flex: none; font-size: .82rem; line-height: 1; }
    .sf-search input { flex: 1 1 auto; box-sizing: border-box; width: 100%; min-width: 0; height: 100%; min-height: 0 !important; margin: 0; padding: 0 !important; border: 0 !important; border-radius: 0 !important; outline: 0 !important; box-shadow: none !important; background: transparent !important; color: var(--ts-text); font: inherit; font-size: .82rem; line-height: 1; -webkit-appearance: none; appearance: none; }
    .sf-search input:focus, .sf-search input:focus-visible { border: 0 !important; outline: 0 !important; box-shadow: none !important; background: transparent !important; }
    .sf-search input::-webkit-search-decoration, .sf-search input::-webkit-search-cancel-button { -webkit-appearance: none; }
    .sf-catalog-body { flex: 1; max-height: 452px; overflow-y: auto; padding-right: .2rem; }
    .sf-icon-group { margin-bottom: .6rem; }
    .sf-icon-group:last-child { margin-bottom: 0; }
    .sf-icon-group h4 { display: flex; align-items: center; gap: .4rem; margin: 0 0 .35rem; color: var(--ts-text-soft); font-size: .7rem; font-weight: 800; text-transform: uppercase; letter-spacing: .04em; }
    .sf-icon-group h4 span { padding: .02rem .35rem; border-radius: 999px; color: var(--ts-text-muted); background: var(--ts-surface); border: 1px solid var(--ts-border); font-size: .63rem; font-weight: 700; }
    .sf-icon-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: .4rem; align-items: stretch; }
    .sf-icon-option { display: flex; flex-direction: column; align-items: center; justify-content: flex-start; gap: .3rem; min-width: 0; min-height: 0 !important; padding: .4rem .3rem; border: 1px solid var(--ts-border); border-radius: 10px; color: var(--ts-text-soft); background: var(--ts-surface); font: inherit; text-align: center; cursor: pointer; transition: border-color .15s ease, background .15s ease, opacity .15s ease; }
    .sf-icon-option:hover:not(:disabled) { border-color: var(--ts-primary); background: var(--ts-bg-blue-400-a8); }
    .sf-icon-option.on { border-color: var(--ts-primary); background: var(--ts-bg-blue-400-a14); color: var(--ts-text); }
    .sf-icon-option:disabled { cursor: not-allowed; opacity: .48; }
    .sf-icon-tile { position: relative; width: 26px; height: 26px; flex: none; display: grid; place-items: center; border-radius: 8px; color: var(--ts-text-blue-250-3); background: var(--ts-bg-blue-400-a14); font-size: .82rem; }
    .sf-icon-option.on .sf-icon-tile { color: var(--ts-text-on-accent); background: var(--ts-primary); }
    .sf-icon-check { position: absolute; right: -4px; bottom: -4px; width: 13px; height: 13px; display: none; place-items: center; border-radius: 50%; color: var(--ts-text-on-accent); background: var(--ts-primary); border: 2px solid var(--ts-surface); font-size: .5rem; }
    .sf-icon-option.on .sf-icon-check { display: grid; }
    .sf-icon-label { min-width: 0; width: 100%; overflow-wrap: break-word; font-size: .69rem; font-weight: 650; line-height: 1.2; }

    .sf-side { display: flex; flex-direction: column; min-width: 0; height: 100%; box-sizing: border-box; padding: 1rem; border: 1px solid var(--ts-border-strong); border-radius: 14px; background: var(--ts-surface); }
    .sf-side-head { display: flex; align-items: flex-start; justify-content: space-between; gap: .8rem; padding-bottom: .65rem; margin-bottom: .65rem; border-bottom: 1px solid var(--ts-border); }
    .sf-side-head h3 { display: flex; align-items: center; flex-wrap: wrap; gap: .45rem; margin: 0 0 .2rem; color: var(--ts-text); font-size: .95rem; }
    .sf-side-head p { margin: 0; color: var(--ts-text-soft); font-size: .77rem; line-height: 1.4; }
    .sf-side-counter { padding: .05rem .45rem; border-radius: 999px; color: var(--ts-text-soft); background: var(--ts-bg-elevated); border: 1px solid var(--ts-border); font-size: .66rem; font-weight: 750; }
    .sf-side-counter.full { color: var(--ts-warning-text); border-color: var(--ts-warning-text); }
    .sf-side-empty { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: .35rem; min-height: 210px; padding: 1.2rem; border: 1px dashed var(--ts-border-strong); border-radius: 12px; background: var(--ts-bg-blue-400-a4); color: var(--ts-text-muted); text-align: center; }
    .sf-side-empty > i { color: var(--ts-text-blue-250-3); font-size: 1.5rem; }
    .sf-side-empty strong { color: var(--ts-text); font-size: .86rem; }
    .sf-side-empty span { max-width: 30ch; font-size: .76rem; line-height: 1.45; }
    .sf-list { list-style: none; flex: 1 1 auto; min-height: 0; margin: 0; padding: 0 .15rem 0 0; display: flex; flex-direction: column; gap: .3rem; overflow-y: auto; }
    .sf-list li { display: grid; grid-template-columns: auto auto minmax(0, 1fr) auto; align-items: center; gap: .45rem; min-width: 0; padding: .25rem .4rem; border-radius: 10px; border: 1px solid var(--ts-border); background: var(--ts-bg-elevated); cursor: grab; transition: border-color .15s ease, opacity .15s ease; }
    .sf-order { width: 19px; height: 19px; display: grid; place-items: center; border-radius: 50%; color: var(--ts-text-muted); border: 1px solid var(--ts-border); font-size: .68rem; font-weight: 800; font-variant-numeric: tabular-nums; }
    .sf-list li.dragging { opacity: .45; }
    .sf-list li.drop-target { border-color: var(--ts-primary); box-shadow: 0 0 0 2px var(--ts-shadow-blue-400-a25-2); }
    .sf-icon { width: 27px; height: 27px; display: grid; place-items: center; flex: none; border-radius: 8px; color: var(--ts-text-blue-250-3); background: var(--ts-bg-blue-400-a14); font-size: .85rem; }
    .sf-text { display: grid; min-width: 0; }
    .sf-text strong { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: var(--ts-text); font-size: .85rem; }
    .sf-text small { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: var(--ts-text-muted); font-size: .72rem; }
    .sf-name { display: flex; align-items: center; gap: .4rem; min-width: 0; }
    .sf-name strong { min-width: 0; }
    .sf-badge { flex: none; padding: .02rem .4rem; border-radius: 999px; color: var(--ts-text-blue-250-3); border: 1px solid var(--ts-primary-border); font-size: .64rem; font-weight: 700; white-space: nowrap; }
    .sf-actions { display: flex; flex-wrap: nowrap; gap: .1rem; }
    .sf-actions .sf-mini { flex: none; width: 24px; height: 24px; min-height: 24px !important; padding: 0; border-color: transparent; background: transparent; color: var(--ts-text-muted); font-size: .72rem; }
    .sf-actions .sf-mini:hover:not(:disabled) { border-color: var(--ts-border-strong); color: var(--ts-text); background: var(--ts-surface); }
    .sf-side-actions { display: grid; gap: .55rem; margin-top: auto; padding-top: .8rem; border-top: 1px solid var(--ts-border); }
    .sf-side-actions-copy { display: grid; gap: .12rem; }
    .sf-side-actions-copy strong { color: var(--ts-text); font-size: .82rem; }
    .sf-side-actions-copy span { color: var(--ts-text-muted); font-size: .72rem; line-height: 1.4; }
    .sf-side-actions-buttons { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: .45rem; }
    .sf-side-actions-buttons .sf-btn { width: 100%; padding: 0 .55rem; }

    .sf-collections { padding-top: .95rem; border-top: 1px solid var(--ts-border); }
    .sf-collections-head { display: flex; align-items: flex-start; justify-content: space-between; gap: .8rem; margin-bottom: .7rem; }
    .sf-collection-actions { display: flex; align-items: center; gap: .4rem; flex-wrap: wrap; }
    .sf-btn, .sf-mini { display: inline-flex; align-items: center; justify-content: center; gap: .4rem; border-radius: 9px; border: 1px solid var(--ts-border-strong); color: var(--ts-text); background: var(--ts-surface); font: inherit; cursor: pointer; }
    .sf-btn { height: 34px; min-height: 34px; padding: 0 .8rem; font-size: .8rem; font-weight: 700; white-space: nowrap; }
    .sf-btn.primary { border-color: transparent; color: var(--ts-text-on-accent); background: linear-gradient(135deg, var(--ts-bg-blue-500), var(--ts-bg-sky-450)); }
    .sf-btn.apply { height: 30px; min-height: 30px; padding: 0 .6rem; color: var(--ts-text-blue-250-3); border-color: var(--ts-primary-border); background: var(--ts-bg-blue-400-a8); font-size: .74rem; }
    .sf-btn.ghost { background: transparent; }
    .sf-btn:disabled, .sf-mini:disabled { opacity: .45; cursor: not-allowed; }
    .sf-mini { width: 30px; height: 30px; min-height: 30px !important; padding: 0; font-size: .82rem; }
    .sf-mini.danger:hover { border-color: var(--ts-danger); color: var(--ts-danger-text); }
    .sf-collection-section { margin-top: .65rem; padding: .65rem; border: 1px solid var(--ts-border); border-radius: 12px; background: var(--ts-bg-blue-400-a4); }
    .sf-collection-section-head { display: flex; align-items: center; gap: .45rem; margin-bottom: .5rem; }
    .sf-collection-section-head strong { color: var(--ts-text); font-size: .82rem; }
    .sf-collection-section-head > span { flex: 1; color: var(--ts-text-muted); font-size: .72rem; }
    .sf-collection-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(245px, 1fr)); gap: .5rem; }
    .sf-empty-group { margin: .15rem 0; color: var(--ts-text-muted); font-size: .75rem; }
    .sf-collection-card { display: grid; grid-template-columns: auto minmax(0, 1fr); align-items: center; gap: .55rem; padding: .55rem .6rem; border: 1px solid var(--ts-border); border-radius: 11px; background: var(--ts-surface); }
    .sf-collection-icons { display: flex; align-items: center; gap: .15rem; }
    .sf-collection-icon { width: 25px; height: 25px; display: grid; place-items: center; border-radius: 7px; color: var(--ts-text-blue-250-3); background: var(--ts-bg-blue-400-a14); font-size: .78rem; }
    .sf-collection-more { color: var(--ts-text-muted); font-size: .68rem; font-weight: 700; }
    .sf-collection-text { display: grid; min-width: 0; }
    .sf-collection-text strong { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: var(--ts-text); font-size: .81rem; }
    .sf-collection-text small { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: var(--ts-text-muted); font-size: .69rem; }
    .sf-collection-card-actions { grid-column: 1 / -1; display: flex; align-items: center; justify-content: flex-end; gap: .25rem; }
    .sf-collection-card-actions .sf-btn.apply { margin-right: auto; }

    .sf-foot { display: flex; align-items: center; justify-content: flex-end; gap: .6rem; margin-top: 1rem; padding-top: 1rem; border-top: 1px solid var(--ts-border); }
    .sf-foot span { margin-right: auto; color: var(--ts-warning-text); font-size: .85rem; font-weight: 600; }

    .sf-overlay { position: fixed; inset: 0; z-index: 3000; display: grid; place-items: center; padding: 1rem; background: var(--ts-bg-navy-950-a78-4); }
    .sf-modal { width: min(720px, 100%); max-height: calc(100dvh - 2rem); overflow-y: auto; box-sizing: border-box; display: flex; flex-direction: column; gap: .45rem; padding: 1.2rem; border-radius: 16px; border: 1px solid var(--ts-border-strong); background: var(--ts-bg-elevated); box-shadow: 0 26px 72px var(--ts-shadow-gray-1000-a40); }
    .sf-group-modal { width: min(430px, 100%); }
    .sf-modal header { display: flex; align-items: center; justify-content: space-between; gap: 1rem; margin-bottom: .3rem; }
    .sf-modal h3 { margin: 0; color: var(--ts-text); font-size: 1.05rem; }
    .sf-modal footer { display: flex; justify-content: flex-end; gap: .5rem; margin-top: .7rem; }
    .sf-label { display: block; margin-top: .55rem; color: var(--ts-text-soft); font-size: .8rem; font-weight: 750; }
    .sf-label small { font-weight: 500; }
    .sf-help { margin: 0; color: var(--ts-text-muted); font-size: .76rem; line-height: 1.45; }
    .sf-input { width: 100%; box-sizing: border-box; height: 40px; min-height: 40px; padding: .6rem .75rem; border-radius: 9px; border: 1px solid var(--ts-border-strong); color: var(--ts-text); background: var(--ts-input-bg); font: inherit; font-size: .9rem; }
    .sf-input:focus { outline: none; border-color: var(--ts-primary); box-shadow: none; }
    .sf-form-row { display: grid; gap: .2rem; }
    .sf-pick-head { display: flex; align-items: baseline; justify-content: space-between; gap: .6rem; }
    .sf-pick-head > span:last-child { color: var(--ts-text-muted); font-size: .75rem; font-weight: 700; }
    .sf-pick-body { max-height: 330px; overflow-y: auto; padding: .5rem; border: 1px solid var(--ts-border); border-radius: 11px; background: var(--ts-bg-blue-400-a4); }
    .compact-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }

    @media (max-width: 1080px) {
      .sf-workspace { grid-template-columns: 1fr; }
      .sf-catalog-body { max-height: 430px; }
      .sf-icon-grid { grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); }
    }

    @media (max-width: 820px) {
      .sf-head, .sf-catalog-head, .sf-collections-head { flex-direction: column; }
      .sf-search { width: 100%; }
      .sf-side-actions-buttons { grid-template-columns: 1fr; }
      .sf-icon-grid { grid-template-columns: repeat(auto-fill, minmax(124px, 1fr)); }
      .compact-grid { grid-template-columns: repeat(auto-fill, minmax(110px, 1fr)); }
    }
  `],
})
export class SpaceFeaturesEditorComponent implements OnInit, OnChanges {
  @Input() spaceId: number | null = null;
  /** true en el formulario de creación: no guarda, solo emite la lista. */
  @Input() staged = false;
  @Output() stagedChange = new EventEmitter<SpaceFeatureInput[]>();
  /** Lista inicial en modo staged (p. ej. al retomar un borrador). */
  @Input() initialStaged: SpaceFeatureInput[] | null = null;

  private readonly sala = inject(SalaService);
  private readonly notifications = inject(NotificationService);
  private readonly dialogs = inject(DialogService);
  private readonly auth = inject(AuthService);

  readonly max = MAX_SPACE_FEATURES;
  readonly items = signal<EditableFeature[]>([]);
  readonly icons = signal<SpaceFeatureIcon[]>([]);
  readonly generals = signal<BusinessFeature[]>([]);
  readonly groups = signal<FeatureCollectionGroup[]>([]);
  readonly collections = signal<FeatureCollection[]>([]);
  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly dirty = signal(false);
  readonly loadError = signal('');
  readonly collectionError = signal('');
  readonly iconQuery = signal('');
  readonly helpOpen = signal(false);

  readonly collectionModalOpen = signal(false);
  readonly collectionBusy = signal(false);
  readonly collectionModalError = signal('');
  collectionForm: CollectionForm = { name: '', description: '', group_id: null, iconIds: [] };
  editingCollectionId: number | null = null;

  readonly groupModalOpen = signal(false);
  readonly groupBusy = signal(false);
  readonly groupModalError = signal('');
  groupForm: GroupForm = { name: '' };
  editingGroupId: number | null = null;

  dragIndex: number | null = null;
  overIndex: number | null = null;
  private nextUid = 1;

  readonly allIconGroups = computed<IconGroup[]>(() => this.buildIconGroups(''));
  readonly iconGroups = computed<IconGroup[]>(() => this.buildIconGroups(this.iconQuery()));

  readonly collectionSections = computed<CollectionSection[]>(() => {
    const all = this.collections();
    const sections: CollectionSection[] = this.groups()
      .map((group) => ({ group, collections: all.filter((collection) => collection.group_id === group.group_id) }));
    const ungrouped = all.filter((collection) => collection.group_id === null);
    if (ungrouped.length) sections.push({ group: null, collections: ungrouped });
    return sections;
  });

  private get businessId(): number | null { return this.auth.currentUser()?.id ?? null; }

  ngOnInit(): void {
    if (this.staged) void this.reload();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['spaceId'] && this.spaceId && !this.staged) void this.reload();
  }

  async reload(): Promise<void> {
    const businessId = this.businessId;
    this.loading.set(true);
    this.loadError.set('');
    this.collectionError.set('');
    try {
      const [icons, generals, features, collectionData] = await Promise.all([
        this.icons().length ? Promise.resolve(this.icons()) : firstValueFrom(this.sala.getSpaceFeatureIcons()),
        businessId ? firstValueFrom(this.sala.getBusinessFeatures(businessId)) : Promise.resolve([] as BusinessFeature[]),
        !this.staged && this.spaceId ? firstValueFrom(this.sala.getSpaceFeatures(this.spaceId)) : Promise.resolve(null),
        businessId
          ? firstValueFrom(this.sala.getFeatureCollections(businessId)).catch(() => {
            this.collectionError.set('No se pudieron cargar las colecciones. Puedes seguir eligiendo iconos.');
            return null;
          })
          : Promise.resolve(null),
      ]);
      this.icons.set(icons);
      this.generals.set(generals);
      if (collectionData) {
        this.groups.set(collectionData.groups);
        this.collections.set(collectionData.collections);
      }
      if (this.staged && this.initialStaged?.length && !this.items().length) {
        // Las generales antiguas se reconstruyen con sus datos actuales; si
        // alguna ya no existe o está oculta, se omite.
        const restored = this.initialStaged.flatMap(input => {
          if ('business_feature_id' in input) {
            const general = generals.find(g => g.business_feature_id === input.business_feature_id && g.is_active);
            return general ? [this.toEditable(general)] : [];
          }
          return input.name ? [this.toEditable({ name: input.name, description: input.description, icon: input.icon || 'check2-circle' })] : [];
        }).slice(0, this.max);
        this.items.set(restored);
        this.stagedChange.emit(this.toInput());
      }
      if (features) {
        this.items.set(features.map((feature: SpaceFeature) => this.toEditable(feature)));
        this.dirty.set(false);
      }
    } catch {
      this.loadError.set('No se pudieron cargar las características.');
    } finally {
      this.loading.set(false);
    }
  }

  private buildIconGroups(query: string): IconGroup[] {
    const key = normalizeName(query);
    const groups: IconGroup[] = [];
    const index = new Map<string, IconGroup>();
    for (const icon of this.icons()) {
      if (key && !normalizeName(`${icon.label} ${icon.group} ${icon.keywords}`).includes(key)) continue;
      let group = index.get(icon.group);
      if (!group) {
        group = { name: icon.group, icons: [] };
        index.set(icon.group, group);
        groups.push(group);
      }
      group.icons.push(icon);
    }
    return groups;
  }

  private toEditable(feature: { business_feature_id?: number | null; name: string; description?: string | null; icon: string }): EditableFeature {
    return { uid: this.nextUid++, business_feature_id: feature.business_feature_id ?? null, name: feature.name, description: feature.description || '', icon: feature.icon };
  }

  private changed(): void {
    this.dirty.set(true);
    if (this.staged) this.stagedChange.emit(this.toInput());
  }

  private toInput(): SpaceFeatureInput[] {
    return this.items().map(item => item.business_feature_id
      ? { business_feature_id: item.business_feature_id }
      : { name: item.name.trim(), description: item.description.trim(), icon: item.icon });
  }

  iconLabel(iconId: string): string {
    return this.icons().find((icon) => icon.id === iconId)?.label ?? iconId;
  }

  isIconSelected(iconId: string): boolean {
    return this.items().some((item) => item.icon === iconId);
  }

  toggleIcon(icon: SpaceFeatureIcon): void {
    if (this.isIconSelected(icon.id)) {
      this.items.update(list => list.filter(item => item.icon !== icon.id));
      this.changed();
      return;
    }
    if (this.items().length >= this.max) {
      this.notifications.error(`Cada espacio muestra hasta ${this.max} amenidades.`);
      return;
    }
    if (this.hasName(icon.label)) {
      this.notifications.error(`Ya tienes una amenidad llamada «${icon.label}».`);
      return;
    }
    this.items.update(list => [...list, { uid: this.nextUid++, business_feature_id: null, name: icon.label, description: '', icon: icon.id }]);
    this.changed();
  }

  clearAll(): void {
    if (!this.items().length) return;
    this.items.set([]);
    this.changed();
  }

  applyCollection(collection: FeatureCollection): void {
    const additions: EditableFeature[] = [];
    const selectedIcons = new Set(this.items().map(item => item.icon));
    const selectedNames = new Set(this.items().map(item => normalizeName(item.name)));
    let omitted = 0;

    for (const iconId of collection.icons) {
      if (this.items().length + additions.length >= this.max) { omitted++; continue; }
      if (selectedIcons.has(iconId) || selectedNames.has(normalizeName(this.iconLabel(iconId)))) { continue; }
      const icon = this.icons().find(candidate => candidate.id === iconId);
      if (!icon) { omitted++; continue; }
      additions.push({ uid: this.nextUid++, business_feature_id: null, name: icon.label, description: '', icon: icon.id });
      selectedIcons.add(icon.id);
      selectedNames.add(normalizeName(icon.label));
    }

    if (additions.length) {
      this.items.update(list => [...list, ...additions]);
      this.changed();
    }
    if (additions.length && omitted) {
      this.notifications.error(`Se agregaron ${additions.length} amenidad(es); ${omitted} no cabían o ya estaban incluidas.`);
    } else if (additions.length) {
      this.notifications.success(`Colección «${collection.name}» aplicada: ${additions.length} amenidad(es) agregada(s).`);
    } else {
      this.notifications.error('No se agregó nada: las amenidades ya estaban incluidas o no queda cupo.');
    }
  }

  private hasName(name: string, exceptUid: number | null = null): boolean {
    const key = normalizeName(name);
    return this.items().some(item => item.uid !== exceptUid && normalizeName(item.name) === key);
  }

  move(index: number, delta: number): void {
    const target = index + delta;
    if (target < 0 || target >= this.items().length) return;
    this.reorder(index, target);
  }

  private reorder(from: number, to: number): void {
    if (from === to) return;
    this.items.update(list => {
      const copy = [...list];
      const [moved] = copy.splice(from, 1);
      copy.splice(to, 0, moved);
      return copy;
    });
    this.changed();
  }

  onDragStart(index: number, event: DragEvent): void {
    this.dragIndex = index;
    event.dataTransfer?.setData('text/plain', String(index));
    if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move';
  }

  onDragOver(index: number, event: DragEvent): void {
    if (this.dragIndex === null) return;
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
    this.overIndex = index;
  }

  onDrop(index: number, event: DragEvent): void {
    event.preventDefault();
    if (this.dragIndex !== null) this.reorder(this.dragIndex, index);
    this.onDragEnd();
  }

  onDragEnd(): void { this.dragIndex = null; this.overIndex = null; }

  remove(index: number): void {
    this.items.update(list => list.filter((_, i) => i !== index));
    this.changed();
  }

  // ---- Colecciones ----
  openCollectionCreate(): void {
    this.editingCollectionId = null;
    this.collectionForm = { name: '', description: '', group_id: null, iconIds: [] };
    this.collectionModalError.set('');
    this.collectionModalOpen.set(true);
  }

  openCollectionEdit(collection: FeatureCollection): void {
    this.editingCollectionId = collection.collection_id;
    this.collectionForm = {
      name: collection.name,
      description: collection.description || '',
      group_id: collection.group_id,
      iconIds: [...collection.icons],
    };
    this.collectionModalError.set('');
    this.collectionModalOpen.set(true);
  }

  closeCollectionModal(): void {
    if (!this.collectionBusy()) this.collectionModalOpen.set(false);
  }

  toggleCollectionIcon(iconId: string): void {
    if (this.collectionForm.iconIds.includes(iconId)) {
      this.collectionForm.iconIds = this.collectionForm.iconIds.filter(id => id !== iconId);
      return;
    }
    if (this.collectionForm.iconIds.length >= this.max) {
      this.collectionModalError.set(`Una colección puede tener hasta ${this.max} amenidades.`);
      return;
    }
    this.collectionForm.iconIds = [...this.collectionForm.iconIds, iconId];
    this.collectionModalError.set('');
  }

  async saveCollection(): Promise<void> {
    const businessId = this.businessId;
    const name = this.collectionForm.name.replace(/\s+/g, ' ').trim();
    this.collectionModalError.set('');
    if (!businessId) { this.collectionModalError.set('No se pudo identificar el negocio.'); return; }
    if (!name) { this.collectionModalError.set('Escribe el nombre de la colección.'); return; }
    if (!this.collectionForm.iconIds.length) { this.collectionModalError.set('Selecciona al menos una amenidad.'); return; }

    this.collectionBusy.set(true);
    try {
      const input = {
        name,
        description: this.collectionForm.description.replace(/\s+/g, ' ').trim(),
        group_id: this.collectionForm.group_id,
        icons: [...this.collectionForm.iconIds],
      };
      if (this.editingCollectionId) {
        const result = await firstValueFrom(this.sala.updateFeatureCollection(businessId, this.editingCollectionId, input));
        this.collections.update(list => list.map(collection => collection.collection_id === result.collection.collection_id ? result.collection : collection));
        this.notifications.success('Colección actualizada.');
      } else {
        const result = await firstValueFrom(this.sala.createFeatureCollection(businessId, input));
        this.collections.update(list => [...list, result.collection]);
        this.notifications.success('Colección creada.');
      }
      this.collectionModalOpen.set(false);
    } catch (error: any) {
      this.collectionModalError.set(typeof error === 'string' ? error : 'No se pudo guardar la colección.');
    } finally {
      this.collectionBusy.set(false);
    }
  }

  async deleteCollection(collection: FeatureCollection): Promise<void> {
    const businessId = this.businessId;
    if (!businessId) return;
    const ok = await this.dialogs.confirm({
      tone: 'danger',
      title: `Eliminar «${collection.name}»`,
      message: 'La colección se eliminará del negocio. Los espacios que ya usan sus amenidades no cambian. Esta acción no se puede deshacer.',
      confirmText: 'Eliminar',
    });
    if (!ok) return;
    try {
      await firstValueFrom(this.sala.deleteFeatureCollection(businessId, collection.collection_id));
      this.collections.update(list => list.filter(item => item.collection_id !== collection.collection_id));
      this.notifications.success('Colección eliminada.');
    } catch (error: any) {
      this.notifications.error(typeof error === 'string' ? error : 'No se pudo eliminar la colección.');
    }
  }

  // ---- Grupos de colecciones ----
  openGroupCreate(): void {
    this.editingGroupId = null;
    this.groupForm = { name: '' };
    this.groupModalError.set('');
    this.groupModalOpen.set(true);
  }

  openGroupEdit(group: FeatureCollectionGroup): void {
    this.editingGroupId = group.group_id;
    this.groupForm = { name: group.name };
    this.groupModalError.set('');
    this.groupModalOpen.set(true);
  }

  closeGroupModal(): void {
    if (!this.groupBusy()) this.groupModalOpen.set(false);
  }

  async saveGroup(): Promise<void> {
    const businessId = this.businessId;
    const name = this.groupForm.name.replace(/\s+/g, ' ').trim();
    this.groupModalError.set('');
    if (!businessId) { this.groupModalError.set('No se pudo identificar el negocio.'); return; }
    if (!name) { this.groupModalError.set('Escribe el nombre del grupo.'); return; }

    this.groupBusy.set(true);
    try {
      if (this.editingGroupId) {
        const result = await firstValueFrom(this.sala.updateFeatureCollectionGroup(businessId, this.editingGroupId, name));
        this.groups.update(list => list.map(group => group.group_id === result.group.group_id ? result.group : group));
        this.notifications.success('Grupo actualizado.');
      } else {
        const result = await firstValueFrom(this.sala.createFeatureCollectionGroup(businessId, name));
        this.groups.update(list => [...list, result.group]);
        this.notifications.success('Grupo creado.');
      }
      this.groupModalOpen.set(false);
    } catch (error: any) {
      this.groupModalError.set(typeof error === 'string' ? error : 'No se pudo guardar el grupo.');
    } finally {
      this.groupBusy.set(false);
    }
  }

  async deleteGroup(group: FeatureCollectionGroup): Promise<void> {
    const businessId = this.businessId;
    if (!businessId) return;
    const ok = await this.dialogs.confirm({
      tone: 'danger',
      title: `Eliminar grupo «${group.name}»`,
      message: 'Las colecciones del grupo no se eliminan: quedarán en «Sin grupo». Esta acción no se puede deshacer.',
      confirmText: 'Eliminar grupo',
    });
    if (!ok) return;
    try {
      await firstValueFrom(this.sala.deleteFeatureCollectionGroup(businessId, group.group_id));
      this.groups.update(list => list.filter(item => item.group_id !== group.group_id));
      this.collections.update(list => list.map(item => item.group_id === group.group_id ? { ...item, group_id: null } : item));
      this.notifications.success('Grupo eliminado.');
    } catch (error: any) {
      this.notifications.error(typeof error === 'string' ? error : 'No se pudo eliminar el grupo.');
    }
  }

  // ---- Ayuda y teclado ----
  toggleHelp(event: Event): void {
    event.stopPropagation();
    this.helpOpen.update(open => !open);
  }

  closeHelp(): void {
    this.helpOpen.set(false);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.helpOpen()) return;
    const target = event.target as HTMLElement | null;
    if (!target?.closest('.sf-help-wrap')) this.helpOpen.set(false);
  }

  onCollectionEnter(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    const target = event.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'SELECT') void this.saveCollection();
  }

  onGroupEnter(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    const target = event.target as HTMLElement;
    if (target.tagName === 'INPUT') void this.saveGroup();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.collectionModalOpen()) this.closeCollectionModal();
    else if (this.groupModalOpen()) this.closeGroupModal();
    else if (this.helpOpen()) this.closeHelp();
  }

  async save(): Promise<void> {
    if (!this.spaceId) return;
    this.saving.set(true);
    try {
      const saved = await firstValueFrom(this.sala.saveSpaceFeatures(this.spaceId, this.toInput()));
      this.items.set(saved.map(feature => this.toEditable(feature)));
      this.dirty.set(false);
      this.notifications.success('Características guardadas.');
    } catch (error: any) {
      this.notifications.error(typeof error === 'string' ? error : 'No se pudieron guardar las características.');
    } finally {
      this.saving.set(false);
    }
  }

  trackByUid(_index: number, item: EditableFeature): number { return item.uid; }
  trackByIcon(_index: number, icon: SpaceFeatureIcon): string { return icon.id; }
  trackByIconGroup(_index: number, group: IconGroup): string { return group.name; }
  trackByGroup(_index: number, group: FeatureCollectionGroup): number { return group.group_id; }
  trackByCollection(_index: number, collection: FeatureCollection): number { return collection.collection_id; }
  trackByCollectionSection(_index: number, section: CollectionSection): string { return section.group ? String(section.group.group_id) : 'ungrouped'; }
}
